/**
 * Configuration the browser reads at start-up.
 *
 * The server injects it into a meta tag at request time, which keeps the Clerk publishable
 * key out of the built image and lets a deployment change keys without a rebuild. When the
 * Vite dev server serves the page the placeholder is still in place, so the values fall back
 * to the `VITE_` environment variables.
 */

/** Values the client needs before it can decide how to authenticate. */
export interface RuntimeConfig {
  /** Clerk publishable key, or an empty string when the server runs development auth. */
  clerkPublishableKey: string;
  /** True when the server trusts a plain email address instead of a Clerk session token. */
  devAuth: boolean;
}

/** Marker left in the meta tag by the build; its presence means the server did not inject. */
const UNREPLACED_PLACEHOLDER = '__RUNTIME_CONFIG__';

/** Reads the injected configuration, falling back to the Vite environment during development. */
export function readRuntimeConfig(): RuntimeConfig {
  const meta = document.querySelector('meta[name="anwesenheit-config"]');
  const content = meta instanceof HTMLMetaElement ? meta.content : '';
  if (content !== '' && content !== UNREPLACED_PLACEHOLDER) {
    try {
      const parsed: unknown = JSON.parse(content);
      if (typeof parsed === 'object' && parsed !== null) {
        const record = parsed as Record<string, unknown>;
        return {
          clerkPublishableKey: typeof record.clerkPublishableKey === 'string' ? record.clerkPublishableKey : '',
          devAuth: record.devAuth === true,
        };
      }
    } catch {
      // A malformed tag is treated as absent; the development fallback below still applies.
    }
  }
  return {
    clerkPublishableKey: import.meta.env.VITE_CLERK_PUBLISHABLE_KEY ?? '',
    devAuth: import.meta.env.VITE_DEV_AUTH === '1',
  };
}
